/**
 * Alouvya i18n — calm, ICU-aware, namespaced.
 *
 * Architecture (per /app/docs/i18n/architecture.md):
 *  - Catalog: /app/frontend/src/locales/{locale}/{namespace}.json
 *  - Source language: en-US (canonical, always 100%)
 *  - Activation: only ACTIVATED locales appear in the public switcher.
 *    Non-activated locales are accessible via ?i18n_preview=1 for
 *    translator/PM review per spec §11.5.
 *  - Fallback chain: locale → language → en-US → key (§3.4)
 *  - Missing-key: dev console.warn + production telemetry counter
 *  - ICU MessageFormat: every variable interpolation goes through
 *    @formatjs/icu-messageformat-parser
 *  - Backward-compatible: existing t("more.title.line1") flat-key calls keep
 *    working via the LEGACY_KEY_MAP below. New code should use
 *    t("parent_app.title_line1") with namespace prefix.
 *
 * The provider is lightweight (~7 KB minified). No react-intl runtime; we wrap
 * intl-messageformat directly for tighter control over fallback + missing-key
 * telemetry.
 */
import { IntlMessageFormat } from "intl-messageformat";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ACTIVATED_LOCALES, LOCALES, RTL_LOCALES, getLocaleLabel } from "./locales-config";

const STORAGE_KEY = "alouvya_lang";
const NAMESPACES = [
  "common",
  "landing",
  "auth",
  "marketing",
  "parent_app",
  "staff_dashboard",
  "admin_panel",
  "errors",
  "admissions",
  "billing",
  "ops",
];

// ── Legacy flat-key bridge (Iteration 29 → iter41 migration) ─────────────────
// Existing call sites pass keys like "more.tile.pickup" or "health.title".
// Until they're migrated to "parent_app.tile.pickup" etc., this map redirects
// the legacy key to its new namespaced path so the screens keep rendering.
const LEGACY_KEY_MAP = {
  // common
  "common.save": "common.save",
  "common.cancel": "common.cancel",
  "common.close": "common.close",
  "common.add": "common.add",
  "common.remove": "common.remove",
  "common.back": "common.back",
  "common.loading": "common.loading",
  "common.error": "common.error",
  "common.signed_in_as": "common.signed_in_as",
  // more page → parent_app
  "more.title.line1": "parent_app.title_line1",
  "more.title.line2": "parent_app.title_line2",
  "more.tile.pickup": "parent_app.tile.pickup",
  "more.tile.pickup.blurb": "parent_app.tile.pickup_blurb",
  "more.tile.absences": "parent_app.tile.absences",
  "more.tile.absences.blurb": "parent_app.tile.absences_blurb",
  "more.tile.moments": "parent_app.tile.moments",
  "more.tile.moments.blurb": "parent_app.tile.moments_blurb",
  "more.tile.gallery": "parent_app.tile.gallery",
  "more.tile.gallery.blurb": "parent_app.tile.gallery_blurb",
  "more.tile.notifications": "parent_app.tile.notifications",
  "more.tile.notifications.blurb": "parent_app.tile.notifications_blurb",
  "more.tile.documents": "parent_app.tile.documents",
  "more.tile.documents.blurb": "parent_app.tile.documents_blurb",
  "more.tile.health": "parent_app.tile.health",
  "more.tile.health.blurb": "parent_app.tile.health_blurb",
  "more.tile.trust": "parent_app.tile.trust",
  "more.tile.trust.blurb": "parent_app.tile.trust_blurb",
  "more.tile.settings": "parent_app.tile.settings",
  "more.tile.settings.blurb": "parent_app.tile.settings_blurb",
  "more.tone.eyebrow": "parent_app.tone_eyebrow",
  "more.change_tone": "parent_app.change_tone",
  "more.contact": "parent_app.contact",
  "more.signout": "parent_app.signout",
  "more.notifications.eyebrow": "parent_app.notifications_eyebrow",
  "more.sign_in_safety.eyebrow": "parent_app.sign_in_safety_eyebrow",
  // settings → admin_panel (cross-surface)
  "settings.language.title": "admin_panel.language.title",
  "settings.language.subtitle": "admin_panel.language.subtitle",
  "settings.language.saved": "admin_panel.language.saved",
  // health → staff_dashboard
  "health.title": "staff_dashboard.title",
  "health.subtitle": "staff_dashboard.subtitle",
  "health.eyebrow.meds": "staff_dashboard.eyebrow_meds",
  "health.eyebrow.events": "staff_dashboard.eyebrow_events",
  "health.eyebrow.history": "staff_dashboard.eyebrow_history",
  "health.add_med": "staff_dashboard.add_med",
  "health.add_event": "staff_dashboard.add_event",
  "health.empty_meds": "staff_dashboard.empty_meds",
  "health.empty_events": "staff_dashboard.empty_events",
  "health.empty_history": "staff_dashboard.empty_history",
  "health.consent_hint": "staff_dashboard.consent_hint",
  "health.field.name": "staff_dashboard.field_name",
  "health.field.dose": "staff_dashboard.field_dose",
  "health.field.unit": "staff_dashboard.field_unit",
  "health.field.frequency": "staff_dashboard.field_frequency",
  "health.field.reason": "staff_dashboard.field_reason",
  "health.field.start": "staff_dashboard.field_start",
  "health.field.end": "staff_dashboard.field_end",
  "health.field.consent": "staff_dashboard.field_consent",
  "health.action.administer": "staff_dashboard.action_administer",
  "health.action.stop": "staff_dashboard.action_stop",
  "health.administered_just_now": "staff_dashboard.administered_just_now",
  "health.parent_only": "staff_dashboard.parent_only",
  "gallery.title": "staff_dashboard.gallery_title",
  "gallery.subtitle": "staff_dashboard.gallery_subtitle",
  "gallery.empty": "staff_dashboard.gallery_empty",
  "gallery.month_count": "staff_dashboard.gallery_month_count",
};

// ── Missing-key telemetry ───────────────────────────────────────────────────
// In dev, console.warn so engineers notice. In prod, emit one
// `i18n.missing_key` event per page-load per key so we don't flood telemetry.
// We also track which (locale) catalogs have FINISHED loading so we don't
// flood with false-positive warnings during the async lazy-load window.
const _missingKeyTrace = new Set();
const _catalogLoaded = new Set(); // populated only AFTER the async load resolves

function _reportMissingKey(key, locale) {
  // Suppress warnings while the requested locale's catalog is still loading.
  // (The first paint queries t() before the lazy import() resolves — this
  // would otherwise emit false-positive missing-key telemetry for every
  // genuinely-present key.)
  if (!_catalogLoaded.has(locale)) return;
  const id = `${locale}::${key}`;
  if (_missingKeyTrace.has(id)) return;
  _missingKeyTrace.add(id);
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV === "development") {
    console.warn(`[i18n] missing key '${key}' in locale '${locale}'`);
  }
  // Production: window.dispatchEvent so a downstream telemetry beacon can pick
  // it up without coupling the i18n core to a specific analytics SDK.
  if (typeof window !== "undefined" && window.dispatchEvent) {
    window.dispatchEvent(
      new CustomEvent("alouvya:i18n_missing_key", { detail: { key, locale } })
    );
  }
}

// ── Catalog loader ──────────────────────────────────────────────────────────
// Lazy import of each (locale, namespace) JSON. webpack/CRA bundles each as a
// separate chunk by default; first paint only fetches the active locale.
const _catalogCache = new Map();

async function _loadNamespace(locale, ns) {
  const key = `${locale}::${ns}`;
  if (_catalogCache.has(key)) return _catalogCache.get(key);
  try {
    const mod = await import(
      /* webpackChunkName: "locale-[request]" */ `../locales/${locale}/${ns}.json`
    );
    _catalogCache.set(key, mod.default || mod);
    return _catalogCache.get(key);
  } catch (_e) {
    _catalogCache.set(key, null);
    return null;
  }
}

// ── Key resolution ──────────────────────────────────────────────────────────
function _resolveKey(catalogs, key) {
  // Key format: "namespace.path.to.string" — namespace is the FIRST segment.
  const parts = key.split(".");
  if (parts.length < 2) return undefined;
  const [ns, ...rest] = parts;
  const cat = catalogs[ns];
  if (!cat) return undefined;
  let val = cat;
  for (const seg of rest) {
    if (val && typeof val === "object" && seg in val) {
      val = val[seg];
    } else {
      return undefined;
    }
  }
  return typeof val === "string" ? val : undefined;
}

// ── Locale resolution priority (spec §3.5) ──────────────────────────────────
function _resolveInitialLocale() {
  // Preview mode lets the URL ?locale= param win over localStorage — useful
  // for translator/PM walkthroughs (`?locale=es-ES&i18n_preview=1` should land
  // on Spanish even if the device previously chose English).
  const preview = _isPreviewModeOn();
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const loc = params.get("locale");
    if (preview && loc && LOCALES[loc]) return loc;
  }
  // 1) Explicit user setting (localStorage)
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES[stored]) return stored;
  } catch (_e) { /* localStorage unavailable */ }
  // 2) URL parameter (non-preview marketing flow)
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const loc = params.get("locale");
    if (loc && LOCALES[loc]) return loc;
  }
  // 3) Accept-Language equivalent (browser navigator.language)
  if (typeof navigator !== "undefined") {
    const nav = navigator.language || navigator.userLanguage;
    if (nav) {
      if (LOCALES[nav]) return nav;
      const lang = nav.split("-")[0];
      const match = Object.keys(LOCALES).find((l) => l.startsWith(`${lang}-`));
      if (match) return match;
    }
  }
  // 4) Platform default
  return "en-US";
}

// ── Preview gate ────────────────────────────────────────────────────────────
// Non-activated locales are HIDDEN from the public switcher unless ?i18n_preview=1
// is set in the URL. This lets translators iterate without exposing first-draft
// content to end users.
function _isPreviewModeOn() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("i18n_preview") === "1") return true;
    return localStorage.getItem("alouvya_i18n_preview") === "1";
  } catch (_e) {
    return false;
  }
}

// ── React context ───────────────────────────────────────────────────────────
const I18nCtx = createContext({
  t: (k) => k,
  locale: "en-US",
  lang: "en",
  setLocale: () => {},
  dir: "ltr",
  languages: [],
  isActivated: () => true,
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(_resolveInitialLocale);
  const [catalogs, setCatalogs] = useState({});
  const [enFallbacks, setEnFallbacks] = useState({});
  // Re-render trigger when async catalog loads finish.
  const [, setBust] = useState(0);

  // Load all namespaces for the active locale + en-US fallback in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [active, fallback] = await Promise.all([
        Promise.all(NAMESPACES.map((ns) => _loadNamespace(locale, ns))),
        locale === "en-US"
          ? Promise.resolve(null)
          : Promise.all(NAMESPACES.map((ns) => _loadNamespace("en-US", ns))),
      ]);
      if (cancelled) return;
      const activeMap = {};
      NAMESPACES.forEach((ns, i) => {
        if (active[i]) activeMap[ns] = active[i];
      });
      setCatalogs(activeMap);
      if (fallback) {
        const fb = {};
        NAMESPACES.forEach((ns, i) => {
          if (fallback[i]) fb[ns] = fallback[i];
        });
        setEnFallbacks(fb);
      } else {
        setEnFallbacks({});
      }
      // Mark this locale's catalogs as fully loaded so missing-key telemetry
      // stops suppressing legitimate warnings.
      _catalogLoaded.add(locale);
      setBust((x) => x + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // Apply lang + dir on <html> for accessibility and CSS logical properties.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale.split("-")[0];
    root.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!LOCALES[next]) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_e) { /* localStorage unavailable */ }
  }, []);

  const t = useCallback(
    (rawKey, vars) => {
      // Map legacy flat key → new namespaced path
      const key = LEGACY_KEY_MAP[rawKey] || rawKey;
      let str = _resolveKey(catalogs, key);
      if (!str) {
        str = _resolveKey(enFallbacks, key);
        if (str) {
          _reportMissingKey(key, locale);
        }
      }
      if (!str) {
        // No translation in active OR fallback — emit missing-key and return the
        // key itself so devs notice in production.
        _reportMissingKey(key, locale);
        return rawKey;
      }
      // ICU MessageFormat — pass vars through the parser only when interpolation
      // is requested. Bare strings skip the parser for speed.
      if (vars && typeof vars === "object" && Object.keys(vars).length > 0) {
        try {
          const msg = new IntlMessageFormat(str, locale);
          return msg.format(vars);
        } catch (_e) {
          // Fall back to naïve {var} replacement if ICU parse fails.
          let out = str;
          for (const [k, v] of Object.entries(vars)) {
            out = out.replaceAll(`{${k}}`, String(v));
          }
          return out;
        }
      }
      return str;
    },
    [catalogs, enFallbacks, locale]
  );

  const previewMode = _isPreviewModeOn();
  const isActivated = useCallback(
    (code) => ACTIVATED_LOCALES.has(code) || previewMode,
    [previewMode]
  );

  // Public language list — autonym labels, filtered by activation gate.
  // Backward-compat: each entry exposes BOTH the new shape (.autonym, .label)
  // and the legacy shape (.native, .code-as-short-lang) so existing call
  // sites (Settings.jsx etc.) keep working unchanged.
  const languages = useMemo(
    () =>
      Object.keys(LOCALES)
        .filter(isActivated)
        .map((code) => ({
          code,
          autonym: LOCALES[code].autonym,
          native: LOCALES[code].autonym, // legacy alias
          label: getLocaleLabel(code),
          dir: RTL_LOCALES.has(code) ? "rtl" : "ltr",
          activated: ACTIVATED_LOCALES.has(code),
        })),
    [isActivated]
  );

  const value = useMemo(
    () => ({
      t,
      locale,
      lang: locale.split("-")[0],
      setLocale,
      // Backward-compat: existing call sites use .setLang(short_code)
      setLang: (lang) => {
        const match =
          Object.keys(LOCALES).find((l) => l === lang) ||
          Object.keys(LOCALES).find((l) => l.startsWith(`${lang}-`));
        if (match) setLocale(match);
      },
      dir: RTL_LOCALES.has(locale) ? "rtl" : "ltr",
      languages,
      isActivated,
    }),
    [t, locale, setLocale, languages, isActivated]
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}

// Re-exports for legacy imports.
export { LOCALES as LANGUAGES };
